// src/app/[lang]/components/LazyBookImage.tsx
"use client";

import React, { useState } from 'react';
import Image from 'next/image';

interface LazyBookImageProps {
  src: string;
  alt: string;
  blurDataURL?: string;
  priority?: boolean;
  sizes: string;
  quality?: number;
  className?: string;
}

/**
 * Book cover image with smooth transition from blur to full image
 * Uses thumbhash blur placeholder while loading
 * Designed for use with Next.js Image fill prop
 */
export default function LazyBookImage({
  src,
  alt,
  blurDataURL,
  priority = false,
  sizes,
  quality = 75,
  className = '',
}: LazyBookImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded">
        <p className="text-gray-600 text-sm">Image failed to load</p>
      </div>
    );
  }

  return (
    <>
      {blurDataURL && (
        <img
          src={blurDataURL}
          alt=""
          className={`absolute inset-0 w-full h-full object-cover ${className}`}
          aria-hidden="true"
        />
      )}
      <Image
        src={src}
        alt={alt}
        fill
        priority={priority}
        loading={priority ? "eager" : "lazy"}
        quality={quality}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        } ${className}`}
        sizes={sizes}
        onLoad={() => setIsLoaded(true)}
        onError={() => {
          console.error('Book image failed to load:', src.substring(0, 50));
          setHasError(true);
        }}
      />
    </>
  );
}
